import {
    AlertColor,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Modal,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { Customer } from "./CustomerAccessories";
import { renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";
import { defaultPaymentTerm } from "../../../common/Constants";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface CustomerProps {
    onClose: Function;
    paymentTerms: DropDownComboOption[];
}

export interface CustomerSingleHandle {
    Initialise: (mode: OpenMode, customer: Customer) => void;
}

export const CustomerSingle = React.forwardRef(
    (props: CustomerProps, ref: React.Ref<CustomerSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');

        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [customer, setCustomer] = React.useState<Customer>();
        const [originalCustomer, setOriginalCustomer] = React.useState<Customer>();
        const [saved, setSaved] = React.useState<boolean>(false);

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, customer: Customer) {
                setOpen(true);
                setSaved(false);
                renewToken();
        
                setCustomer(customer);

                hasChanges.current = false;
                setOpenMode(mode);
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalCustomer);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalCustomer);
            }
        }

        function OnCustomerChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...customer };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setCustomer(temp);
                }
            }
        }

        function OnTradingCompanyChecked(event: React.ChangeEvent<HTMLInputElement>) {
            let temp: any = { ...customer };
            temp.customerType = event.target.checked ? 2 : 1;
            setCustomer(temp);
            hasChanges.current = true;
        }
        
        function SaveCustomer(close: boolean) {
            if (hasChanges.current)
            {
                if (customer)
                {
                    if (customer.companyCode == null || customer.companyCode == '')
                    {
                        showSnackBar("Please enter a company code for the customer.", "", "error");
                        return;
                    }

                    if ((customer.companyNameJpn == null || customer.companyNameJpn == '') &&
                        (customer.companyNameEng == null || customer.companyNameEng == '') &&
                        (customer.companyNameChi == null || customer.companyNameChi == '')
                    )
                    {
                        showSnackBar("Please enter a name for the customer.", "", "error");
                        return;
                    }

                    let c = {...customer};
                    c.companyNameJpn = customer.companyNameJpn ?? "";
                    c.companyNameEng = customer.companyNameEng ?? "";
                    c.companyNameChi = customer.companyNameChi ?? "";
                    c.contactPerson = (customer.contactPerson && customer.contactPerson != undefined) ? customer.contactPerson : "";
                    c.phone1 = (customer.phone1 && customer.phone1 != undefined) ? customer.phone1 : "";
                    c.phone2 = (customer.phone2 && customer.phone2 != undefined) ? customer.phone2 : "";
                    c.fax1 = (customer.fax1 && customer.fax1 != undefined) ? customer.fax1 : "";
                    c.fax2 = (customer.fax2 && customer.fax2 != undefined) ? customer.fax2 : "";
                    c.email = (customer.email && customer.email != undefined) ? customer.email : "";
                    c.mobile = (customer.mobile && customer.mobile != undefined) ? customer.mobile : "";
                    c.address = (customer.address && customer.address != undefined) ? customer.address : "";
                    c.deliveryAddress = (customer.deliveryAddress && customer.deliveryAddress != undefined) ? customer.deliveryAddress : "";
                    c.paymentTerm = customer.paymentTerm ?? "";

                    if (openMode === OpenMode.EDIT)
                    {
                        axios.put<Customer>('Api/V1/Customer/', c).then(
                            (result) => {
                                showSnackBar("Customer saved successfully.", "", "success");
                                hasChanges.current = false;
                                setSaved(true);
                                setCustomer(result.data);
                                setOriginalCustomer(result.data);
                                setOpen(!close);
                                setDialogOpen(false);
                                if (close)
                                    props.onClose(true, result.data);
                            },
                            (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                            }
                        );
                    } 
                    else
                    {
                        axios.post<Customer>('Api/V1/Customer/', c).then(
                            (result) => {
                                showSnackBar("Customer created successfully.", "", "success");
                                hasChanges.current = false;
                                setSaved(true);
                                setCustomer(result.data);
                                setOriginalCustomer(result.data);
                                setOpenMode(OpenMode.EDIT);
                                setOpen(!close);
                                setDialogOpen(false);
                                if (close)
                                    props.onClose(true, result.data);
                            },
                            (error) => {
                                showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                            }
                        );
                    }
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMainMedium}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Customer" />

                                <div className={style.ModalHeading}>
                                    <Typography variant={ tinyScreen ? "h6" : (smallScreen ? "h5" : "h4") }>Customer</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Customer Code"
                                            value = {customer?.companyCode ?? ""}
                                            onChange={(e) => OnCustomerChanged("companyCode", e.target.value.slice(0, 10))}
                                            size="small"
                                        />
                                        <div className={style.ModalContentFieldOneThirdWidth}>
                                            <FormControlLabel
                                                control={<Checkbox checked={customer?.customerType==2} onChange={(e) => OnTradingCompanyChecked(e)} />}
                                                label="Trading Company"
                                            />
                                        </div>
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Name (中国語)"
                                            value = {customer?.companyNameChi ?? ""}
                                            onChange={(e) => OnCustomerChanged("companyNameChi", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Name (日本語)"
                                            value = {customer?.companyNameJpn ?? ""}
                                            onChange={(e) => OnCustomerChanged("companyNameJpn", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Name (英語)"
                                            value = {customer?.companyNameEng ?? ""}
                                            onChange={(e) => OnCustomerChanged("companyNameEng", e.target.value.slice(0, 80))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Contact Person"
                                            value = {customer?.contactPerson ?? ""}
                                            onChange={(e) => OnCustomerChanged("contactPerson", e.target.value.slice(0, 30))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Phone 1"
                                            value = {customer?.phone1 ?? ""}
                                            onChange={(e) => OnCustomerChanged("phone1", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Phone 2"
                                            value = {customer?.phone2 ?? ""}
                                            onChange={(e) => OnCustomerChanged("phone2", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Mobile"
                                            value = {customer?.mobile ?? ""}
                                            onChange={(e) => OnCustomerChanged("mobile", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Fax 1"
                                            value = {customer?.fax1 ?? ""}
                                            onChange={(e) => OnCustomerChanged("fax1", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Fax 2"
                                            value = {customer?.fax2 ?? ""}
                                            onChange={(e) => OnCustomerChanged("fax2", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            label="Email"
                                            value = {customer?.email ?? ""}
                                            onChange={(e) => OnCustomerChanged("email", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldOneThirdWidth}
                                            id="payment-term"
                                            value={customer?.paymentTerm ?? defaultPaymentTerm}
                                            onChange={(e) => OnCustomerChanged("paymentTerm", e.target.value)}
                                            select
                                            label="Payment Term"
                                            size="small"
                                            >
                                            {props.paymentTerms.map((item) => (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.value}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Address"
                                            value = {customer?.address ?? ""}
                                            multiline
                                            rows={3}
                                            onChange={(e) => OnCustomerChanged("address", e.target.value.slice(0, 250))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Customer Address"
                                            value = {customer?.deliveryAddress ?? ""}
                                            multiline
                                            rows={3}
                                            onChange={(e) => OnCustomerChanged("deliveryAddress", e.target.value.slice(0, 250))}
                                            size="small"
                                        />
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveCustomer(false);
                                            }
                                        }
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveCustomer(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


